import ErrorOutline from '@mui/icons-material/ErrorOutline';
import { Box, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';

const AMBER = {
    chipBg: '#FFFBEB',
    border: '#FDE68A',
    text: '#B45309',
    icon: '#F59E0B'
};

export type SecondaryDataSourceWarningProps = {
    existsInSecondaryDataSource?: boolean;
    // Name of the secondary system the employee is missing from (e.g. 'Planday').
    // Falls back to a generic label when unknown (the cross-company list).
    provider?: string;
    // 'icon' keeps list rows compact (glyph + tooltip); 'chip' is the fuller
    // "Not in {provider}" pill shown next to the name on the user detail page.
    variant?: 'icon' | 'chip';
};

// A resigned employee is often left behind in the primary source while already
// removed from the secondary system, so a user missing from the secondary source
// is the actionable sync/setup case worth flagging (US-3365). Amber, not red:
// this is a setup gap, distinct from a genuine error.
export const SecondaryDataSourceWarning = ({
    existsInSecondaryDataSource,
    provider,
    variant = 'icon'
}: SecondaryDataSourceWarningProps) => {
    const { t } = useTranslation();

    if (existsInSecondaryDataSource !== false) return null;

    const providerLabel = provider ?? t('secondary_source_generic_provider');

    if (variant === 'chip') {
        return (
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    height: 26,
                    padding: '0 11px 0 9px',
                    borderRadius: '999px',
                    backgroundColor: AMBER.chipBg,
                    border: `1px solid ${AMBER.border}`,
                    color: AMBER.text,
                    fontSize: '13px',
                    fontWeight: 600,
                    width: 'fit-content'
                }}
            >
                <ErrorOutline sx={{ fontSize: 15, color: AMBER.icon }} />
                {t('not_in_provider', { provider: providerLabel })}
            </Box>
        );
    }

    return (
        <Tooltip
            title={t('secondary_source_tooltip', { provider: providerLabel })}
        >
            <ErrorOutline sx={{ fontSize: 18, color: AMBER.icon }} />
        </Tooltip>
    );
};
